import { useEffect } from 'react';
import { inputService } from '../services/InputService';
import { hubService } from '../services/HubService';
import { useGameStore } from '../store/gameSlice';
import { useHubStore } from '../store/hubSlice';
import { useCinematicStore } from '../store/cinematicSlice';
import { HUB_PHYSICS } from '../data/hub.config';
import { DirectionEnum } from '../types/enums';
import { registerHubInput } from './useGameInput';
import { openInterface } from '../services/InterfaceService';
import { openNpc } from '../services/NpcInteractionService';
import { useInputContext } from './useInput';

/**
 * Input vector → 8-dir facing. ix/iy ∈ {-1, 0, 1}. Returns null for (0, 0),
 * in which case the current facing is kept.
 */
function directionFromVector(ix: number, iy: number): DirectionEnum | null {
  if (ix === 0 && iy === 0) {
    return null;
  }
  if (ix === 0) {
    return iy < 0 ? DirectionEnum.TOP : DirectionEnum.BOTTOM;
  }
  if (iy === 0) {
    return ix < 0 ? DirectionEnum.LEFT : DirectionEnum.RIGHT;
  }
  if (ix < 0) {
    return iy < 0 ? DirectionEnum['TOP-LEFT'] : DirectionEnum['BOTTOM-LEFT'];
  }
  return iy < 0 ? DirectionEnum['TOP-RIGHT'] : DirectionEnum['BOTTOM-RIGHT'];
}

function approach(current: number, target: number, step: number): number {
  if (current < target) {
    return Math.min(current + step, target);
  }
  if (current > target) {
    return Math.max(current - step, target);
  }
  return current;
}

const INV_SQRT2 = 1 / Math.SQRT2;

function resetHubInputState(): void {
  hubService.vx = 0;
  hubService.vy = 0;
}

export function useHubInput() {
  useInputContext('dungeon', {
    onSuspended: resetHubInputState,
  });

  useEffect(() => {
    const process = () => {
      const { gameplay } = useGameStore.getState();
      if (gameplay !== 'hub') {
        return;
      }
      if (useCinematicStore.getState().active) {
        return;
      }
      if (!useHubStore.getState().mapLoaded) {
        return;
      }

      if (inputService.isActionJustPressed('dungeon', 'INVENTORY')) {
        openInterface('inventory');
        return;
      }

      const left = inputService.isActionActive('dungeon', 'MOVE_LEFT');
      const right = inputService.isActionActive('dungeon', 'MOVE_RIGHT');
      const up = inputService.isActionActive('dungeon', 'MOVE_UP');
      const down = inputService.isActionActive('dungeon', 'MOVE_DOWN');
      let ix = (right ? 1 : 0) - (left ? 1 : 0);
      let iy = (down ? 1 : 0) - (up ? 1 : 0);

      // Diagonal normalization to keep the same resulting speed.
      if (ix !== 0 && iy !== 0) {
        ix *= INV_SQRT2;
        iy *= INV_SQRT2;
      }

      const { maxSpeed, accel, decel, playerCollision } = HUB_PHYSICS;
      const tvx = ix * maxSpeed;
      const tvy = iy * maxSpeed;

      const stepX = ix !== 0 ? accel : decel;
      const stepY = iy !== 0 ? accel : decel;
      hubService.vx = approach(hubService.vx, tvx, stepX);
      hubService.vy = approach(hubService.vy, tvy, stepY);

      // Move axis-by-axis so the player slides along walls instead of stopping.
      const px = hubService.playerX;
      const py = hubService.playerY;

      const candX = px + hubService.vx;
      if (
        hubService.vx !== 0 &&
        !hubService.isSolidAabb(candX, py, playerCollision) &&
        !hubService.getBlockingNpc(candX, py, playerCollision)
      ) {
        hubService.playerX = candX;
      } else {
        hubService.vx = 0;
      }

      const candY = hubService.playerY + hubService.vy;
      if (
        hubService.vy !== 0 &&
        !hubService.isSolidAabb(hubService.playerX, candY, playerCollision) &&
        !hubService.getBlockingNpc(hubService.playerX, candY, playerCollision)
      ) {
        hubService.playerY = candY;
      } else {
        hubService.vy = 0;
      }

      // Collision-trigger doors: stepping onto one switches hub map. switchMap
      // ignores re-entrant calls, so holding into the door won't re-fire.
      const stepDoor = hubService.getCollisionDoor(
        hubService.playerX,
        hubService.playerY,
        playerCollision,
      );
      if (stepDoor) {
        void hubService.switchMap(stepDoor.targetMapId, stepDoor.targetSpawnId);
        return;
      }

      const dir = directionFromVector(
        ix !== 0 ? Math.sign(ix) : 0,
        iy !== 0 ? Math.sign(iy) : 0,
      );
      if (dir !== null) {
        hubService.facing = dir;
      }

      if (inputService.isActionJustPressed('dungeon', 'INTERACT')) {
        const npc = hubService.getInteractableNpc(
          hubService.playerX,
          hubService.playerY,
        );
        if (npc) {
          openNpc(npc.npcId);
          return;
        }
        const door = hubService.getInteractableDoor(
          hubService.playerX,
          hubService.playerY,
        );
        if (door) {
          void hubService.switchMap(door.targetMapId, door.targetSpawnId);
        }
      }
    };

    registerHubInput(process);
    return () => registerHubInput(null);
  }, []);
}
